/**
 * Chat Service - LiveKit Integration
 * 
 * Integrates with Labeah AI chat service using LiveKit text streams
 */

import { Room, RoomEvent, Track } from 'livekit-client';
import keepAlive from './keepAlive.js';

class ChatService {
  constructor() {
    this.isConnected = false;
    this.listeners = [];
    this.sessionResetListeners = []; // Listeners for session reset events
    this.agentStateListeners = []; // Listeners for agent state changes
    
    // LiveKit
    this.room = null;
    this.localParticipant = null;
    
    // Configuration
    this.shareToken = null; // Will be set based on language
    this.domain = typeof window !== 'undefined' ? window.location.hostname : 'app.labeah.ai';
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    
    // Language support
    this.currentLanguage = null;
    
    // Cached data (verified once on load)
    this.verifiedData = null;
    
    // Session identifiers
    this.identity = null;
    this.roomName = null;
    this.sessionId = null;
    this.currentSessionId = null; // Track current session
    
    // Flags to prevent duplicate operations
    this.isInitializing = false;
    this.isConnecting = false;
    
    // Initialize user UUID on app load
    this.initializeUserUUID();
  }

  /**
   * Initialize user UUID on app load (creates if doesn't exist)
   */
  initializeUserUUID() {
    const userUUID = this.getUserUUID();
    console.log('🔑 User UUID initialized:', userUUID);
    return userUUID;
  }

  /**
   * Set the current language and corresponding token
   */
  setLanguage(language) {
    console.log('🌐 Setting language to:', language);
    this.currentLanguage = language;
    
    // Set the appropriate token based on language
    if (language === 'ar') {
      this.shareToken = import.meta.env.VITE_SHARE_TOKEN_AR;
    } else {
      this.shareToken = import.meta.env.VITE_SHARE_TOKEN_EN;
    }
    
    // Clear verified data to force re-initialization with new token
    this.verifiedData = null;
    
    console.log('✅ Language set to:', language, 'Token:', this.shareToken ? '✓' : '✗');
  }

  /**
   * Initialize service - verify token and get agent details
   */
  async initialize(language = 'ar') {
    // Set language and token if not already set or if language changed
    if (this.currentLanguage !== language) {
      this.setLanguage(language);
    }

    // Check if already initialized with same language
    if (this.verifiedData && this.currentLanguage === language) {
      console.log('✅ Already initialized (using cached data)');
      return { success: true };
    }

    // Check if currently initializing
    if (this.isInitializing) {
      console.log('⏳ Initialization in progress, waiting...');
      // Wait for current initialization to complete
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return { success: true };
    }

    this.isInitializing = true;

    try {
      console.log('🔐 Verifying token and fetching agent details...');
      
      if (!this.shareToken) {
        throw new Error('No share token available for language: ' + language);
      }
      
      // Verify token (now includes agent details in response)
      const verifyResponse = await fetch(`${this.apiBaseUrl}/v1/tokens/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: this.shareToken,
          domain: this.domain,
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error(`Token verification failed: ${verifyResponse.status}`);
      }

      this.verifiedData = await verifyResponse.json();
      
      if (!this.verifiedData.valid) {
        throw new Error(`Token invalid: ${this.verifiedData.reason}`);
      }

      console.log('✅ Token verified and agent details loaded:', this.verifiedData);
      console.log('✅ Agent info:', {
        agentName: this.verifiedData.access.agentName,
        agentCode: this.verifiedData.access.agentCode,
        textAgentCode: this.verifiedData.access.textAgentCode,
        audioAgentCode: this.verifiedData.access.audioAgentCode,
      });

      return { success: true };
    } catch (error) {
      console.error('❌ Initialization failed:', error);
      return { success: false, error };
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Get or create persistent user UUID
   */
  getUserUUID() {
    const STORAGE_KEY = 'labeah_user_uuid';
    
    // Try to get existing UUID from localStorage
    let savedUUID = null;
    try {
      savedUUID = localStorage.getItem(STORAGE_KEY);
      if (savedUUID) {
        console.log('✅ Using saved user UUID:', savedUUID);
      }
    } catch (error) {
      console.warn('⚠️ localStorage not available:', error);
    }
    
    // If no saved UUID, generate new one
    if (!savedUUID) {
      savedUUID = this.generateUUID();
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, savedUUID);
        console.log('💾 Saved new user UUID:', savedUUID);
      } catch (error) {
        console.warn('⚠️ Could not save UUID to localStorage:', error);
      }
    }
    
    return savedUUID;
  }

  /**
   * Get or create persistent user identity
   */
  getUserIdentity() {
    const STORAGE_KEY = 'labeah_user_identity';
    
    // Try to get existing identity from localStorage
    let savedIdentity = null;
    try {
      savedIdentity = localStorage.getItem(STORAGE_KEY);
      if (savedIdentity) {
        console.log('✅ Using saved user identity:', savedIdentity);
      }
    } catch (error) {
      console.warn('⚠️ localStorage not available:', error);
    }
    
    // If no saved identity, generate new one
    if (!savedIdentity) {
      const timestamp = Date.now();
      const random4Digits = Math.floor(1000 + Math.random() * 9000);
      savedIdentity = `guest-${timestamp}-${random4Digits}`;
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, savedIdentity);
        console.log('💾 Saved new user identity:', savedIdentity);
      } catch (error) {
        console.warn('⚠️ Could not save identity to localStorage:', error);
      }
    }
    
    return savedIdentity;
  }

  /**
   * Clear saved user identity and UUID (force new identity on next session)
   */
  clearUserIdentity() {
    const IDENTITY_KEY = 'labeah_user_identity';
    const UUID_KEY = 'labeah_user_uuid';
    try {
      localStorage.removeItem(IDENTITY_KEY);
      localStorage.removeItem(UUID_KEY);
      console.log('🗑️ User identity and UUID cleared from storage');
    } catch (error) {
      console.warn('⚠️ Could not clear identity:', error);
    }
  }

  /**
   * Generate unique identifiers for session
   */
  generateSessionIdentifiers() {
    const timestamp = Date.now();
    const random4Digits = Math.floor(1000 + Math.random() * 9000);
    
    // Use persistent identity
    this.identity = this.getUserIdentity();
    this.sessionId = `session-${timestamp}-${Math.floor(1000 + Math.random() * 9000)}`;
    
    // Generate room name with UUID v4
    const uuid = this.generateUUID();
    this.roomName = `chat-${uuid}-${timestamp}-${random4Digits}`;
    
    console.log('🆔 Session identifiers:', {
      identity: this.identity,
      sessionId: this.sessionId,
      roomName: this.roomName
    });
  }

  /**
   * Generate UUID v4
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Generate LiveKit token
   */
  async generateLiveKitToken() {
    try {
      console.log('🎫 Generating LiveKit token...');
      
      this.generateSessionIdentifiers();
      
      // Get persistent user UUID
      const userUUID = this.getUserUUID();
      console.log('🆔 User UUID:', userUUID);
      
      const { organizationId, agentId, versionId, textAgentCode, agentCode } = this.verifiedData.access;

      console.log('🔧 Agent configuration:', {
        textAgentCode,
        agentCode,
        fullAgentDetails: this.verifiedData.access,
      });

      const payload = {
        identity: this.identity,
        roomName: this.roomName,
        participantName: this.identity,
        agentName: textAgentCode, // Using textAgentCode for text-based responses
        roomPrefix: 'chat',
        shareToken: this.shareToken,
        domain: this.domain,
        metadata: {
          organization_id: String(organizationId),
          agent_id: agentId,
          agent_version_id: versionId,
          type: 'text', // Explicitly requesting text mode
          channel: 'app',
          direction: 'inbound',
          is_debug: false,
          user_id: this.identity,
          user_uuid: userUUID, // Add persistent user UUID
          session_id: this.sessionId,
          widget_id: '0',
          // Add explicit text mode flags
          mode: 'text',
          output_modality: 'text',
        },
        permissions: {
          canPublish: true,
          canPublishData: true,
          canSubscribe: true,
          canUpdateOwnMetadata: true,
        },
        storeMapping: false,
      };
      
      console.log('📤 Token request payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${this.apiBaseUrl}/v2/commkit/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate token: ${response.status}`);
      }

      const tokenData = await response.json();
      console.log('✅ LiveKit token generated:', tokenData);
      console.log('🔧 Token details:', {
        roomName: tokenData.roomName,
        agentName: tokenData.agentName,
        wsUrl: tokenData.wsUrl,
        metadata: tokenData.metadata,
      });
      
      // Decode and log the JWT token to see what's configured
      try {
        const tokenParts = tokenData.accessToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('🔐 Decoded JWT payload:', payload);
        console.log('🤖 Room config agents:', payload.roomConfig?.agents);
      } catch (e) {
        console.log('⚠️ Could not decode JWT:', e);
      }

      return tokenData;
    } catch (error) {
      console.error('❌ Failed to generate LiveKit token:', error);
      throw error;
    }
  }

  /**
   * Connect to LiveKit room
   */
  async connect(forceNewSession = false, language = 'ar') {
    // Check if already connected
    if (this.isConnected && this.room && !forceNewSession && this.currentLanguage === language) {
      console.log('✅ Already connected to LiveKit');
      return { success: true };
    }

    // If forcing new session or language changed, disconnect first
    if ((forceNewSession || this.currentLanguage !== language) && this.room) {
      console.log('🔄 Force new session or language change, disconnecting old session...');
      this.disconnect();
    }

    // Check if currently connecting
    if (this.isConnecting) {
      console.log('⏳ Connection in progress, waiting...');
      while (this.isConnecting) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return { success: this.isConnected };
    }

    this.isConnecting = true;

    try {
      // Initialize if not done yet or language changed
      if (!this.verifiedData || this.currentLanguage !== language) {
        const initResult = await this.initialize(language);
        if (!initResult.success) {
          throw new Error('Initialization failed');
        }
      }

      // Generate LiveKit token
      const tokenData = await this.generateLiveKitToken();

      console.log('🔌 Connecting to LiveKit...');

      // Create room instance with text stream support and transcription
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Enable automatic track subscription
        autoSubscribe: true,
        // WebSocket timeout settings to keep connection alive
        websocketTimeout: 30000, // 30 seconds
        // Enable audio playback to receive agent's audio
        audioOutput: {
          deviceId: 'default',
        },
      });

      // Set up event listeners BEFORE connecting
      this.setupRoomEventListeners();

      // Connect to room
      await this.room.connect(tokenData.wsUrl, tokenData.accessToken);

      this.localParticipant = this.room.localParticipant;
      this.isConnected = true;

      console.log('✅ Connected to LiveKit room:', this.roomName);
      console.log('👤 Local participant:', this.localParticipant.identity);
      console.log('🎧 Text stream handlers active for: lk.chat, lk.transcription');
      
      // Log all current remote participants and their tracks
      this.room.remoteParticipants.forEach((participant) => {
        console.log('👥 Remote participant:', participant.identity);
        console.log('📦 Track publications:', Array.from(participant.trackPublications.values()).map(p => ({
          kind: p.kind,
          trackName: p.trackName,
          subscribed: !!p.track,
        })));
      });

      // Track new session
      const newSessionId = this.sessionId;
      const isNewSession = this.currentSessionId !== newSessionId;
      
      if (isNewSession) {
        console.log('🆕 New session started:', newSessionId);
        console.log('🗑️ Notifying listeners to clear old session data');
        
        this.currentSessionId = newSessionId;
        
        // Notify all session reset listeners to clear their state
        this.sessionResetListeners.forEach(callback => {
          console.log('📢 Calling session reset listener');
          callback({
            sessionId: newSessionId,
            roomName: this.roomName,
            identity: this.identity,
          });
        });
      }

      // Start keep-alive to prevent connection timeout
      keepAlive.start(this.room);

      return { success: true };
    } catch (error) {
      console.error('❌ Failed to connect to LiveKit:', error);
      this.isConnected = false;
      return { success: false, error };
    } finally {
      this.isConnecting = false;
    }
  }


  /**
   * Set up LiveKit room event listeners for text streams
   */
  setupRoomEventListeners() {
    if (!this.room) return;

    console.log('🎧 Setting up text stream handlers');
    console.log('🎧 Current listeners count:', this.listeners.length);

    // Register text stream handler for lk.chat (primary handler)
    this.room.registerTextStreamHandler('lk.chat', async (reader, participantInfo) => {
      console.log('📨 [lk.chat] Text stream from:', participantInfo.identity);
      
      // Check if message is from agent (not self)
      const isAgent = participantInfo.identity?.startsWith('agent-') || 
                     participantInfo.identity?.includes('assistant') ||
                     participantInfo.identity !== this.identity;
      
      if (!isAgent || participantInfo.identity === this.identity) {
        console.log('ℹ️ Skipping message from self:', participantInfo.identity);
        return;
      }
      
      try {
        // Read the complete text from the stream
        const message = await reader.readAll();
        
        console.log('📨 Received message (plain text):', message);
        console.log('📢 Active listeners:', this.listeners.length);
        
        // Filter out DEBUG messages
        if (message && message.toLowerCase().includes('debug')) {
          console.log('ℹ️ Skipping DEBUG message');
          return;
        }
        
        // Remove source references [SOURCE:...]
        const cleanedMessage = message ? message.replace(/\[source:[^\]]+\]/gi, '').trim() : message;
        
        // Notify listeners with plain text
        this.listeners.forEach((callback, index) => {
          console.log(`📢 Calling listener #${index + 1}`);
          callback({
            id: reader.info.id || Date.now(),
            text: cleanedMessage, // Plain text, not parsed
            sender: 'bot',
            timestamp: new Date(reader.info.timestamp).toISOString(),
            participant: participantInfo.identity,
            topic: 'lk.chat',
          });
        });
        
        console.log('✅ Message delivered to all listeners');
      } catch (error) {
        console.error('❌ Error processing lk.chat stream:', error);
      }
    });
    
    // Register handler for lk.transcription (note: transcription, not transcript!)
    this.room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      console.log('📨 [lk.transcription] Text stream from:', participantInfo.identity);
      
      // Filter out self-messages
      if (participantInfo.identity === this.identity) {
        console.log('ℹ️ Skipping transcription from self');
        return;
      }
      
      try {
        const message = await reader.readAll();
        console.log('📨 Received transcription:', message);
        
        // Filter out DEBUG messages
        if (message && message.toLowerCase().includes('debug')) {
          console.log('ℹ️ Skipping DEBUG message');
          return;
        }
        
        // Remove source references [SOURCE:...]
        const cleanedMessage = message ? message.replace(/\[source:[^\]]+\]/gi, '').trim() : message;
        
        // Notify listeners
        this.listeners.forEach((callback) => {
          callback({
            id: reader.info.id || Date.now(),
            text: cleanedMessage,
            sender: 'bot',
            timestamp: new Date(reader.info.timestamp).toISOString(),
            participant: participantInfo.identity,
            topic: 'lk.transcription',
          });
        });
      } catch (error) {
        console.error('❌ Error processing lk.transcription stream:', error);
      }
    });
    
    // Catch-all handler for debugging
    this.room.registerTextStreamHandler(undefined, async (reader, participantInfo) => {
      console.log('📨 [CATCH-ALL] Text stream on topic:', reader.info.topic, 'from:', participantInfo.identity);
    });
    
    console.log('✅ All text stream handlers registered (lk.chat, lk.transcription, catch-all)');

    // Log ALL room events for debugging
    Object.values(RoomEvent).forEach(eventName => {
      this.room.on(eventName, (...args) => {
        if (!['ConnectionQualityChanged', 'ActiveDeviceChanged'].includes(eventName)) {
          console.log(`🔔 [EVENT] ${eventName}`, args.length > 0 ? args : '');
        }
      });
    });

    // Handle track subscribed to see what tracks are available
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('📡 Track subscribed:', {
        kind: track.kind,
        source: track.source,
        name: publication.trackName,
        participant: participant.identity,
      });
      
      // If it's an audio track from the agent, log it
      if (track.kind === Track.Kind.Audio) {
        console.log('🎤 Audio track subscribed from:', participant.identity);
        console.log('🎤 Will listen for transcriptions from this track');
      }
    });
    
    // Handle audio track published by remote participant
    this.room.on(RoomEvent.TrackPublished, (publication, participant) => {
      console.log('📣 Track published by remote participant:', {
        kind: publication.kind,
        source: publication.source,
        trackName: publication.trackName,
        participant: participant.identity,
      });
    });
    
    // Handle transcription received (for audio-to-text)
    this.room.on(RoomEvent.TranscriptionReceived, (transcriptions, participant, publication) => {
      console.log('📝 Transcription received from:', participant?.identity);
      console.log('📝 Number of transcriptions:', transcriptions.length);
      
      transcriptions.forEach((transcription, idx) => {
        console.log(`📝 Transcription #${idx + 1}:`, {
          text: transcription.text,
          language: transcription.language,
          trackId: transcription.trackId,
          isFinal: transcription.final,
        });
        
        // Only process final transcriptions
        if (transcription.final && transcription.text) {
          console.log('✅ Final transcription, notifying listeners');
          
          // Notify listeners
          this.listeners.forEach((callback, index) => {
            console.log(`📢 Calling listener #${index + 1} with transcription`);
            callback({
              id: Date.now(),
              text: transcription.text,
              sender: 'bot',
              timestamp: new Date().toISOString(),
              participant: participant?.identity,
              source: 'transcription',
              trackId: transcription.trackId,
            });
          });
        }
      });
    });

    // Also handle data received as fallback for non-stream data
    this.room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      console.log(
        `📨 [${new Date().toISOString()}] Data packet received\n` +
        `  From: ${participant?.identity}\n` +
        `  Topic: ${topic}\n` +
        `  Kind: ${kind}\n` +
        `  Size: ${payload.byteLength} bytes\n` +
        `  Active listeners: ${this.listeners.length}`
      );
      
      try {
        const decoder = new TextDecoder();
        const text = decoder.decode(payload);
        console.log('📨 Decoded data packet:', text);
        
        let data;
        try {
          data = JSON.parse(text);
          console.log('📨 Parsed data packet:', data);
        } catch {
          console.log('📨 Data packet is plain text');
          data = { text: text };
        }

        // Notify listeners
        console.log('📢 Notifying', this.listeners.length, 'listener(s) of data packet');
        this.listeners.forEach((callback, index) => {
          console.log(`📢 Calling listener #${index + 1} for data packet`);
          callback({
            id: Date.now(),
            text: data.text || data.message || text,
            sender: 'bot',
            timestamp: data.timestamp || new Date().toISOString(),
            participant: participant?.identity,
            topic: topic,
          });
        });
      } catch (error) {
        console.error('❌ Error parsing received data:', error);
      }
    });

    // Handle track published
    this.room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      console.log('📤 Track published:', publication.kind);
    });

    // Handle track unpublished
    this.room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
      console.log('📤 Track unpublished:', publication.kind);
    });

    // Handle participant connected
    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('👤 Participant connected:', participant.identity);
      console.log('👥 Total participants:', this.room.remoteParticipants.size + 1);
      console.log('📋 Participant attributes:', participant.attributes);
      console.log('📋 Participant metadata:', participant.metadata);
      
      // Subscribe to participant's tracks
      participant.trackPublications.forEach((publication) => {
        if (publication.track) {
          console.log('🔗 Auto-subscribed to track:', publication.track.kind, publication.trackName);
        } else {
          console.log('📦 Track publication (not yet subscribed):', publication.kind, publication.trackName);
        }
      });
      
      // Listen for new tracks published by this participant
      participant.on(RoomEvent.TrackPublished, (publication) => {
        console.log('📣 Participant published new track:', {
          kind: publication.kind,
          trackName: publication.trackName,
          participant: participant.identity,
        });
      });
      
      // Listen for track subscribed on this specific participant
      participant.on(RoomEvent.TrackSubscribed, (track, publication) => {
        console.log('📡 Track subscribed from participant:', {
          participant: participant.identity,
          kind: track.kind,
          trackName: publication.trackName,
        });
        
        if (track.kind === Track.Kind.Audio) {
          console.log('🎤 Audio track ready, waiting for transcription or text stream...');
        }
      });
    });

    // Handle participant attributes changed (might contain messages!)
    this.room.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes, participant) => {
      console.log('📝 Participant attributes changed:', participant.identity);
      console.log('📝 Changed attributes:', changedAttributes);
      console.log('📝 All participant attributes:', participant.attributes);
      
      // Check for agent state changes
      if (changedAttributes['lk.agent.state']) {
        const agentState = changedAttributes['lk.agent.state'];
        console.log('🤖 Agent state changed to:', agentState);
        
        // Notify agent state listeners
        this.agentStateListeners.forEach((callback) => {
          callback(agentState);
        });
      }
      
      // Check if there's a message in the attributes
      if (changedAttributes.message || changedAttributes.text || changedAttributes.response) {
        console.log('🎯 Found message in participant attributes!');
        const messageText = changedAttributes.message || changedAttributes.text || changedAttributes.response;
        
        // Notify listeners
        this.listeners.forEach((callback, index) => {
          console.log(`📢 Calling listener #${index + 1} with attribute message`);
          callback({
            id: Date.now(),
            text: messageText,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            participant: participant.identity,
            source: 'participantAttributes',
          });
        });
      }
    });
    
    // Handle participant disconnected
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('👋 Participant disconnected:', participant.identity);
    });

    // Handle room disconnected
    this.room.on(RoomEvent.Disconnected, (reason) => {
      console.log('🔌 Disconnected from room. Reason:', reason);
      this.isConnected = false;
      
      // Don't attempt automatic reconnection - wait for user action
      console.log('⚠️ Connection lost. Will reconnect when user sends next message.');
    });
    
    // Handle reconnecting state
    this.room.on(RoomEvent.Reconnecting, () => {
      console.log('🔄 Reconnecting to room...');
    });
    
    // Handle reconnected state  
    this.room.on(RoomEvent.Reconnected, () => {
      console.log('✅ Reconnected to room');
      this.isConnected = true;
    });

    // Handle connection state changes
    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('🔄 Connection state:', state);
      
      // Update connection status
      if (state === 'connected') {
        this.isConnected = true;
      } else if (state === 'disconnected') {
        this.isConnected = false;
      }
    });

    // Handle connection quality changes
    this.room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      console.log('📶 Connection quality:', quality, participant?.identity || 'local');
    });
  }

  /**
   * Send a message via LiveKit text stream
   * Uses sendText API for proper text streaming
   */
  async sendMessage(message) {
    // Check connection and reconnect if needed
    if (!this.isConnected || !this.room) {
      console.log('⚠️ Not connected, reconnecting...');
      const connectResult = await this.connect();
      if (!connectResult.success) {
        throw new Error('Failed to connect');
      }
    }

    try {
      console.log('📤 Sending message via text stream:', message);
      console.log('📤 Current room state:', {
        connected: this.isConnected,
        roomName: this.roomName,
        identity: this.identity,
        participantCount: this.room.remoteParticipants.size + 1
      });

      // Filter out DEBUG messages
      if (message.toLowerCase().includes('debug')) {
        console.log('ℹ️ Skipping DEBUG message');
        return { success: true, messageId: Date.now() };
      }
      
      // Remove source references [SOURCE:...]
      const cleanedMessage = message.replace(/\[source:[^\]]+\]/gi, '').trim();

      // Send plain text (NOT JSON) to match working implementation
      const result = await this.room.localParticipant.sendText(
        cleanedMessage, // Send plain text directly
        {
          topic: 'lk.chat',
          attributes: {
            messageType: 'user',
            timestamp: new Date().toISOString(),
          },
        }
      );

      console.log('✅ Message sent via text stream (plain text)');
      console.log('📊 Send result:', result);

      return {
        success: true,
        messageId: Date.now(),
      };
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      console.error('❌ Error details:', error.message, error.stack);
      return { success: false, error };
    }
  }

  /**
   * Disconnect from LiveKit
   */
  disconnect() {
    console.log('👋 Disconnecting from chat service...');
    
    // Stop keep-alive
    keepAlive.stop();
    
    if (this.room) {
      try {
        // Unregister text stream handlers
        this.room.unregisterTextStreamHandler('lk.chat');
        this.room.unregisterTextStreamHandler('lk.transcription');
        console.log('🗑️ Text stream handlers unregistered');
        
        this.room.disconnect();
      } catch (error) {
        console.error('Error during disconnect:', error);
      }
      this.room = null;
    }
    
    this.localParticipant = null;
    this.isConnected = false;
    this.isConnecting = false;
    
    // Clear session identifiers
    this.identity = null;
    this.roomName = null;
    this.sessionId = null;
    // currentSessionId is kept to detect new sessions on next connect
    
    // Don't clear listeners - we might reconnect
    // this.listeners = [];
    // this.sessionResetListeners = [];
    
    console.log('👋 Disconnected from chat service');
  }
  
  /**
   * Start a new session (disconnect old, connect new, clear history)
   */
  async startNewSession(forceNewIdentity = false) {
    console.log('🆕 Starting new LiveKit session...');
    
    // Clear user identity if requested
    if (forceNewIdentity) {
      console.log('🔄 Forcing new user identity...');
      this.clearUserIdentity();
    }
    
    // Disconnect existing session
    if (this.room) {
      this.disconnect();
    }
    
    // Wait a bit for cleanup
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Connect with force new session flag
    return await this.connect(true);
  }
  
  /**
   * Check if connection is alive
   */
  isConnectionAlive() {
    return this.isConnected && this.room && this.room.state === 'connected';
  }

  /**
   * Listen for incoming messages
   */
  onMessage(callback) {
    console.log('📝 Registering message listener. Current count:', this.listeners.length);
    this.listeners.push(callback);
    console.log('📝 New listener count:', this.listeners.length);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing message listener. Current count:', this.listeners.length);
      this.listeners = this.listeners.filter(cb => cb !== callback);
      console.log('🗑️ New listener count:', this.listeners.length);
    };
  }

  /**
   * Listen for session reset events (when new LiveKit session starts)
   */
  onSessionReset(callback) {
    console.log('📝 Registering session reset listener');
    this.sessionResetListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing session reset listener');
      this.sessionResetListeners = this.sessionResetListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Listen for agent state changes (thinking, speaking, listening)
   */
  onAgentStateChange(callback) {
    console.log('📝 Registering agent state listener');
    this.agentStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing agent state listener');
      this.agentStateListeners = this.agentStateListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      identity: this.identity,
      roomName: this.roomName,
      sessionId: this.sessionId,
    };
  }
}

// Export singleton instance
export default new ChatService();